import { THEMES } from '@/shared/types/preferences.types';
import type { ThemePreference } from '@/shared/types/preferences.types';

/**
 * A device-local copy of the signed-in user's chosen theme.
 *
 * The preference itself is owned by the server, which cannot answer before the app has
 * painted. Without a hint to start from, anyone who has chosen a palette that differs from
 * their device's gets a flash of the wrong one on every load — around 200ms of white before
 * a dark app appears. This is only a hint: the settings response remains authoritative and
 * overwrites it.
 *
 * `system` is stored like any other value even though it needs no hint, so that switching
 * back to it clears a stale `dark` rather than leaving it to be honoured forever.
 */
const STORAGE_KEY = 'conbent.display.theme';

function isTheme(value: string): value is ThemePreference {
  return (THEMES as readonly string[]).includes(value);
}

/** Written by whoever was last signed in, so it is dropped when the session ends. */
export function readCachedTheme(): ThemePreference | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw !== null && isTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeCachedTheme(theme: ThemePreference): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage being unavailable costs a themed first paint, nothing more.
  }
}

export function clearCachedTheme(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // As above.
  }
}
